import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import type { LabelHTMLAttributes, ReactNode } from "react";
import { isFieldRequiredForSubmit } from "../../utils/schema-fields";
import { cn } from "../lib/utils";
import { PartialIndicator, Text } from "../primitives";
import { RequiredForApprovalIndicator } from "../ui/RequiredForApprovalIndicator";

export type FormLabelProps = LabelHTMLAttributes<HTMLLabelElement> & {
	partial?: boolean;
	requiredForApproval?: boolean;
	/** When set with a FormSchemaProvider schema, derives hard-required asterisk unless `required` is passed. */
	name?: string;
	/**
	 * Explicit hard-required asterisk. Wins over schema derivation from `name`.
	 *
	 * @deprecated Prefer `name` + `FormSchemaProvider` so the asterisk is schema-derived.
	 * Explicit `required` will be replaced under
	 * [Form Standardization](https://linear.app/inrev/project/form-standardization-a77aa9131bef).
	 */
	required?: boolean;
	noSpace?: boolean;
	condensed?: boolean;
	subLabel?: ReactNode;
};

/**
 * Shared label used by both FormComponent and FormLineItem.
 * Renders the label text alongside partial/required-for-approval indicator badges and an optional sub-label.
 */
export const FormItemLabel = ({
	partial,
	requiredForApproval,
	name,
	required: requiredProp,
	condensed,
	subLabel,
	noSpace,
	children,
	...props
}: FormLabelProps) => {
	const schema = useFormSchema();
	const required =
		requiredProp ?? (name && schema ? isFieldRequiredForSubmit(schema, name) : false);

	return (
		<div
			className={cn(Boolean(subLabel) && "flex flex-col space-y-[8px]", condensed && "space-y-0")}
		>
			<div className="relative flex items-center space-x-[10px] h-fit">
				<label
					{...props}
					className={cn(
						"text-[14.75px] text-gray-800/95 font-[515] flex items-center",
						condensed && "text-[13px] font-[515] space-y-[0px] leading-loose",
						props.className,
					)}
				>
					{required ? (
						<span>
							{children}
							<Text color="danger">{"\u00A0"}*</Text>
						</span>
					) : (
						children
					)}
				</label>

				{partial && <PartialIndicator active={true} className="flex-shrink-0" />}
				{requiredForApproval && (
					<RequiredForApprovalIndicator active={true} className="flex-shrink-0" />
				)}
				{!partial && !requiredForApproval && !noSpace && (
					<div className="h-4 w-4 block ml-[10px] flex-shrink-0" />
				)}
			</div>
			{subLabel && (
				<div className="text-[13px] text-tertiary font-normal !mb-[5px] italic">{subLabel}</div>
			)}
		</div>
	);
};
